import React, { useState } from "react";
import { Text, Flex, Label, Input, SxProp, Button, ThemeUICSSProperties, Box } from "theme-ui";

import { Icon } from "../Icon";

type RowProps = SxProp & {
  label: string | React.ReactNode;
  trailingLabel?: React.ReactNode;
  children?: React.ReactNode;
  labelId?: string;
  labelFor?: string;
  infoIcon?: React.ReactNode;
};

export const Row: React.FC<RowProps> = ({
  sx,
  label,
  trailingLabel,
  labelId,
  labelFor,
  children,
  infoIcon
}) => {
  return (
    <Flex sx={{ alignItems: "stretch", position: "relative", width: "100%", ...sx }}>
      <Label
        id={labelId}
        htmlFor={labelFor}
        sx={{
          p: 0,
          px: 3,
          pt: "12px",
          position: "absolute",
          fontSize: "18px",
          border: 1,
          color: "#000000",
          fontWeight: "medium",
          lineHeight: 1,
          borderColor: "transparent",
          width: "100%"
        }}
      >
        <Flex sx={{ width: "100%" }}>
          <Flex sx={{ alignItems: "center" }}>
            {label}
            {infoIcon && infoIcon}
          </Flex>
          {trailingLabel && (
            <Box ml="auto" sx={{ fontSize: "12px", fontWeight: "400", color: "#808080" }}>
              {trailingLabel}
            </Box>
          )}
        </Flex>
      </Label>
      {children}
    </Flex>
  );
};

type PendingAmountProps = {
  value: string;
};

const PendingAmount: React.FC<PendingAmountProps & SxProp> = ({ sx, value }) => (
  <Text {...{ sx }}>
    (
    {value === "++" ? (
      <Icon name="angle-double-up" />
    ) : value === "--" ? (
      <Icon name="angle-double-down" />
    ) : value?.startsWith("+") ? (
      <>
        <Icon name="angle-up" /> {value.substr(1)}
      </>
    ) : value?.startsWith("-") ? (
      <>
        <Icon name="angle-down" /> {value.substr(1)}
      </>
    ) : (
      value
    )}
    )
  </Text>
);

type StaticAmountsProps = {
  inputId?: string;
  labelledBy?: string;
  amount?: string;
  unit?: string;
  color?: string;
  trailingText?: string;
  trailingTextColor?: string;
  pendingAmount?: string;
  pendingColor?: string;
  onClick?: () => void;
  children?: React.ReactNode;
};

export const StaticAmounts: React.FC<StaticAmountsProps & SxProp> = ({
  sx,
  inputId,
  labelledBy,
  amount,
  unit,
  color,
  trailingText,
  trailingTextColor,
  pendingAmount,
  pendingColor,
  onClick,
  children
}) => {
  return (
    <Flex
      id={inputId}
      aria-labelledby={labelledBy}
      {...{ onClick }}
      sx={{
        justifyContent: "space-between",
        alignItems: "center",

        ...(onClick ? { cursor: "text" } : {}),

        ...staticStyle,
        ...sx
      }}
    >
      {amount && (
        <Flex sx={{ alignItems: "center" }}>
          <Text sx={{ color, fontSize: 2, fontWeight: "medium" }}>{amount}</Text>

          {unit && (
            <>
              &nbsp;
              <Text sx={{ fontWeight: "medium", fontSize: 2, color: "#808080" }}>{unit}</Text>
            </>
          )}

          {pendingAmount && (
            <>
              &nbsp;
              <PendingAmount
                sx={{ color: pendingColor, opacity: 0.8, fontSize: "0.666em" }}
                value={pendingAmount}
              />
            </>
          )}

          {trailingText && (
            <>
              &nbsp;
              <Text sx={{ fontWeight: "normal", fontSize: 2, color: trailingTextColor }}>{trailingText}</Text>
            </>
          )}
        </Flex>
      )}

      {children}
    </Flex>
  );
};

const staticStyle: ThemeUICSSProperties = {
  flexGrow: 1,

  mb: 0,
  pl: 3,
  pr: "11px",
  pb: 0,
  pt: "28px",

  fontSize: "18px",

  border: 1,
  borderColor: "transparent"
};

const editableStyle: ThemeUICSSProperties = {
  flexGrow: 1,

  mb: [2, 3],
  pl: 3,
  pr: "11px",
  pb: 2,
  pt: "28px",

  fontSize: "18px",

  boxShadow: [1, 2],
  border: 1,
  borderRadius: "4px",
  color: "#000000",
  fontWeight: "medium",
  background: "white",
  borderColor: "#666666"
};

type StaticRowProps = RowProps & StaticAmountsProps;

export const StaticRow: React.FC<StaticRowProps> = ({
  label,
  labelId,
  labelFor,
  infoIcon,
  amount,
  children,
  ...props
}) => (
  <Row
    label={label}
    labelId={labelId}
    labelFor={labelFor}
    infoIcon={infoIcon}
    sx={{ mt: [-2, -3], pb: [2, 3] }}
  >
    {amount ? (
      <StaticAmounts amount={amount} {...props}>
        {children}
      </StaticAmounts>
    ) : (
      children
    )}
  </Row>
);

type DisabledEditableRowProps = Omit<StaticAmountsProps, "labelledBy" | "onClick"> & {
  label: string;
  trailingLabel?: React.ReactNode;
};

export const DisabledEditableAmounts: React.FC<StaticAmountsProps & SxProp> = ({
  inputId,
  children,
  sx,
  ...props
}) => (
  <StaticAmounts
    sx={{ ...editableStyle, boxShadow: 0, ...sx }}
    labelledBy={`${inputId}-label`}
    inputId={inputId}
    {...props}
  >
    {children}
  </StaticAmounts>
);

export const DisabledEditableRow: React.FC<DisabledEditableRowProps> = ({
  inputId,
  label,
  trailingLabel,
  amount,
  children,
  ...props
}) => (
  <Row labelId={`${inputId}-label`} label={label} trailingLabel={trailingLabel}>
    {amount ? (
      <DisabledEditableAmounts inputId={inputId} amount={amount} {...props}>
        {children}
      </DisabledEditableAmounts>
    ) : (
      children
    )}
  </Row>
);

type EditableRowProps = DisabledEditableRowProps & {
  editingState: [string | undefined, (editing: string | undefined) => void];
  editedAmount: string;
  setEditedAmount: (editedAmount: string) => void;
  maxAmount?: string;
  maxedOut?: boolean;
};

export const EditableRow: React.FC<EditableRowProps> = ({
  label,
  trailingLabel,
  inputId,
  unit,
  amount,
  color,
  pendingAmount,
  pendingColor,
  editingState,
  editedAmount,
  setEditedAmount,
  maxAmount,
  maxedOut
}) => {
  const [editing, setEditing] = editingState;
  const [invalid, setInvalid] = useState(false);

  return editing === inputId ? (
    <Row {...{ label, labelFor: inputId, unit, trailingLabel }}>
      <Input
        autoFocus
        id={inputId}
        type="number"
        step="any"
        defaultValue={editedAmount}
        {...{ invalid }}
        onChange={e => {
          try {
            setEditedAmount(e.target.value);
            setInvalid(false);
          } catch {
            setInvalid(true);
          }
        }}
        onBlur={() => {
          setEditing(undefined);
          setInvalid(false);
        }}
        variant="editor"
        sx={{
          ...editableStyle,
          fontWeight: "medium",
          background: "white",
          color: invalid ? "#B00020" : "#000000",
          "&:focus": {
            borderColor: invalid ? "#B00020" : "#F0B90B",
            outline: "none"
          }
        }}
      />
    </Row>
  ) : (
    <Row labelId={`${inputId}-label`} {...{ label, unit, trailingLabel }}>
      <StaticAmounts
        sx={{
          ...editableStyle,
          bg: invalid ? "invalid" : "background"
        }}
        labelledBy={`${inputId}-label`}
        onClick={() => setEditing(inputId)}
        {...{ inputId, amount, unit, color, pendingAmount, pendingColor, invalid }}
      >
        {maxAmount && (
          <Button
            sx={{
              fontSize: 0,
              p: "4px 8px",
              width: "48px",
              textTransform: "uppercase",
              fontFamily: "Avenir"
            }}
            variant="small"
            onClick={event => {
              setEditedAmount(maxAmount);
              event.stopPropagation();
            }}
            disabled={maxedOut}
          >
            max
          </Button>
        )}
      </StaticAmounts>
    </Row>
  );
};
